import { Button } from '@solinkcorp/solink-ui/components/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@solinkcorp/solink-ui/components/Dialog';

import { X } from 'lucide-react';
import { type ReactNode } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  cancelText: string;
  confirmText?: string;
  confirmVariant?: 'default' | 'destructive';
  onClose: () => void;
  onConfirm?: () => Promise<unknown> | void;
  dataTestId?: string;
}

function formatDescription(description: ReactNode) {
  if (typeof description !== 'string') {
    return description;
  }

  const questionAt = description.indexOf('?');
  if (questionAt === -1) {
    return description;
  }

  const lead = description.slice(0, questionAt + 1).trim();
  const detail = description.slice(questionAt + 1).trim();

  return (
    <>
      <p>{lead}</p>
      {detail ? <p>{detail}</p> : null}
    </>
  );
}

export { formatDescription };

/**
 * Compact confirm overlay that mirrors the add-widget dialog: title + X,
 * muted body, then footer actions — sized down to Dialog `xs`.
 */
const ConfirmDialog = ({
  open,
  title,
  description,
  cancelText,
  confirmText,
  confirmVariant = 'default',
  onClose,
  onConfirm,
  dataTestId,
}: ConfirmDialogProps) => {
  const body = formatDescription(description);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        data-testid={dataTestId}
        size="xs"
        showCloseButton={false}
        className="tw flex w-[360px] max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden bg-background-surface p-0"
      >
        {/*
          Custom chrome instead of DialogHeader/DialogFooter: those components bleed
          to the edges with -mx-6/-mt-6 (and -mx-6/-mb-6) assuming DialogContent kept
          its default p-6, which this full-bleed (p-0) layout doesn't — see the
          comment on dialogContentVariants in solink-ui's Dialog.tsx, and
          CopyToAgentDialog for the same pattern.
        */}
        <div className="tw flex shrink-0 flex-row items-center justify-between gap-2 border-b border-border-muted px-4 py-3 text-left">
          <div className="tw min-w-0">
            <DialogTitle className="tw heading-md">{title}</DialogTitle>
            <DialogDescription className="tw sr-only">
              {typeof description === 'string' ? description : title}
            </DialogDescription>
          </div>
          <DialogClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Close"
              className="tw appearance-none border-none bg-transparent text-foreground-secondary shadow-none outline-none hover:bg-background-elevated focus-visible:ring-0"
            >
              <X className="tw size-4" />
            </Button>
          </DialogClose>
        </div>
        <div className="tw bg-background-muted px-4 py-4 text-center text-foreground-primary body-md">
          <div className="tw flex flex-col gap-2">{body}</div>
        </div>
        <div className="tw flex shrink-0 flex-col-reverse gap-2 border-t border-border-muted px-4 py-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            {cancelText}
          </Button>
          {confirmText ? (
            <Button
              type="button"
              variant={confirmVariant}
              className={confirmVariant === 'default' ? 'tw' : undefined}
              onClickAsync={async () => {
                try {
                  await onConfirm?.();
                } catch {
                  // The button reports a second error when this callback rejects.
                  return;
                }
                onClose();
              }}
            >
              {confirmText}
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
